import { Fragment, useEffect, useState } from "react";
import { retrySubmissionHooks } from "../../../api/forms";
import { ApiError } from "../../../api/client";
import { IconRefresh } from "../../../components/icons";
import {
  HOOK_LOGS_PER_PAGE_OPTIONS,
  hookLogRowKey,
  useFormHookLogsQuery,
} from "../../../hooks/useFormHookLogsQuery";
import { useSubmissionHookLogsQuery } from "../../../hooks/useSubmissionHookLogsQuery";
import type { HookLogDto } from "../../../types/hookLog";
import type { FormSubmissionDto } from "../../../types/submission";
import { FieldLabel, SelectInput } from "../_components/AdminShell";
import { IconButton } from "../_components/IconButton";
import { FormsListPagination } from "../forms-list/FormsListPagination";

function formatDateTime(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms} мс`;
  return `${(ms / 1000).toFixed(2)} с`;
}

function prettyJson(raw: string | null): string {
  if (!raw) return "";
  const trimmed = raw.trim();
  if (!trimmed) return "";
  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch {
    return raw;
  }
}

function StatusBadge({
  success,
  status,
}: {
  success: boolean;
  status: number | null;
}) {
  if (success) {
    return (
      <span className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 font-onest text-[11px] font-medium text-emerald-700">
        {status ?? "OK"}
      </span>
    );
  }

  return (
    <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 font-onest text-[11px] font-medium text-red-700">
      {status ?? "Ошибка"}
    </span>
  );
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  if (maxLength <= 1) return "…";
  const head = Math.ceil((maxLength - 1) / 2);
  const tail = Math.floor((maxLength - 1) / 2);
  return `${text.slice(0, head)}…${text.slice(-tail)}`;
}

function formatHookLogUrlDisplay(url: string, maxLength = 36): string {
  if (!url.trim()) return "—";

  let display: string;
  try {
    const parsed = new URL(url);
    const path = `${parsed.pathname}${parsed.search}`;
    display = path && path !== "/" ? path : parsed.host;
  } catch {
    display = url;
  }

  return truncateText(display, maxLength);
}

function HookLogDetails({ log }: { log: HookLogDto }) {
  return (
    <div className="space-y-3 border-t border-slate-100 bg-slate-50/50 px-4 py-3">
      {log.deliveryId ? (
        <p className="font-onest text-xs text-slate-600">
          <span className="text-slate-500">Delivery ID:</span>{" "}
          <code className="rounded bg-white px-1 py-px font-mono text-[11px]">
            {log.deliveryId}
          </code>
        </p>
      ) : null}

      {log.errorMessage ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="font-onest text-xs font-medium text-red-800">Ошибка</p>
          <p className="mt-1 font-onest text-xs text-red-700">
            {log.errorMessage}
          </p>
        </div>
      ) : null}

      {log.requestBody ? (
        <div>
          <p className="mb-1 font-onest text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Тело запроса
          </p>
          <pre className="max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white p-3 font-mono text-[11px] leading-relaxed text-slate-700">
            {prettyJson(log.requestBody)}
          </pre>
        </div>
      ) : null}

      {log.responseBody ? (
        <div>
          <p className="mb-1 font-onest text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Тело ответа
          </p>
          <pre className="max-h-48 overflow-auto rounded-lg border border-slate-200 bg-white p-3 font-mono text-[11px] leading-relaxed text-slate-700">
            {prettyJson(log.responseBody)}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function HookLogTableRow({
  log,
  showSubmission,
  expanded,
  isRetrying,
  onToggle,
  onFilterSubmission,
  onRetry,
}: {
  log: HookLogDto;
  showSubmission: boolean;
  expanded: boolean;
  isRetrying: boolean;
  onToggle: () => void;
  onFilterSubmission: (submissionId: number) => void;
  onRetry: () => void;
}) {
  const colSpan = showSubmission ? 7 : 6;

  return (
    <Fragment>
      <tr
        className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50/80"
        onClick={onToggle}
      >
        {showSubmission ? (
          <td className="px-3 py-2.5 align-middle">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                onFilterSubmission(log.submissionId);
              }}
              className="font-onest text-xs font-medium text-brand-700 hover:underline"
            >
              #{log.submissionId}
            </button>
          </td>
        ) : null}
        <td className="px-3 py-2.5 align-middle font-onest text-xs font-medium text-slate-700">
          #{log.attempt}
        </td>
        <td className="px-3 py-2.5 align-middle">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            <span className="shrink-0 rounded bg-slate-100 px-1.5 py-px font-onest text-[10px] font-semibold text-slate-500">
              {log.method}
            </span>
            <span className="truncate font-mono text-[11px] text-slate-600">
              {formatHookLogUrlDisplay(log.url)}
            </span>
          </div>
        </td>
        <td className="px-3 py-2.5 align-middle">
          <StatusBadge success={log.success} status={log.responseStatus} />
        </td>
        <td className="px-3 py-2.5 align-middle font-onest text-xs text-slate-500">
          {formatDuration(log.durationMs)}
        </td>
        <td className="px-3 py-2.5 align-middle font-onest text-xs text-slate-500">
          {formatDateTime(log.createdAt)}
        </td>
        <td className="py-2.5 pl-2 pr-4 align-middle">
          <div className="flex justify-end">
            <IconButton
              title={`Повторить отправку для ответа #${log.submissionId}`}
              size="sm"
              variant="brand"
              disabled={isRetrying}
              onClick={(event) => {
                event.stopPropagation();
                onRetry();
              }}
            >
              <IconRefresh
                size={14}
                className={isRetrying ? "animate-spin" : undefined}
              />
            </IconButton>
          </div>
        </td>
      </tr>
      {expanded ? (
        <tr className="border-b border-slate-100 last:border-b-0">
          <td colSpan={colSpan} className="p-0">
            <HookLogDetails log={log} />
          </td>
        </tr>
      ) : null}
    </Fragment>
  );
}

interface SubmissionHookLogsProps {
  formId: number;
  submissions: FormSubmissionDto[];
  filterSubmissionId: number | null;
  onFilterSubmission: (submissionId: number | null) => void;
}

export function SubmissionHookLogs({
  formId,
  submissions,
  filterSubmissionId,
  onFilterSubmission,
}: SubmissionHookLogsProps) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(HOOK_LOGS_PER_PAGE_OPTIONS[0]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [retryingKey, setRetryingKey] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setPage(1);
    setExpandedKey(null);
  }, [filterSubmissionId, perPage]);

  const singleSubmissionView = filterSubmissionId != null;

  const allLogsQuery = useFormHookLogsQuery({
    formId,
    page,
    perPage,
    filterSubmissionId: null,
    enabled: !singleSubmissionView,
  });

  const singleLogsQuery = useSubmissionHookLogsQuery({
    formId,
    submissionId: filterSubmissionId,
    enabled: singleSubmissionView,
  });

  const logs = singleSubmissionView ? singleLogsQuery.logs : allLogsQuery.logs;
  const meta = singleSubmissionView ? null : allLogsQuery.meta;
  const isLoading = singleSubmissionView
    ? singleLogsQuery.isLoading
    : allLogsQuery.isLoading;
  const error = singleSubmissionView
    ? singleLogsQuery.error
    : allLogsQuery.error;

  const reload = () => {
    if (singleSubmissionView) singleLogsQuery.reload();
    else allLogsQuery.reload();
  };

  const handleRowRetry = async (log: HookLogDto) => {
    const rowKey = hookLogRowKey(log);
    setRetryingKey(rowKey);
    setActionError(null);
    setActionMessage(null);

    try {
      await retrySubmissionHooks(formId, log.submissionId);
      setActionMessage(
        `Повторная отправка для ответа #${log.submissionId} запущена`,
      );
      reload();
    } catch (err) {
      setActionError(
        err instanceof ApiError
          ? err.message
          : "Не удалось повторить отправку вебхуков",
      );
    } finally {
      setRetryingKey(null);
    }
  };

  const handlePerPageChange = (nextPerPage: number) => {
    setPerPage(nextPerPage);
    setPage(1);
  };

  return (
    <div>
      <div className="border-b border-slate-100 px-4 py-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1">
            <FieldLabel size="compact" htmlFor="hook-log-submission">
              Фильтр по ответу
            </FieldLabel>
            <SelectInput
              id="hook-log-submission"
              size="compact"
              value={
                filterSubmissionId != null ? String(filterSubmissionId) : ""
              }
              onChange={(value) => {
                onFilterSubmission(value ? Number(value) : null);
                setExpandedKey(null);
                setActionMessage(null);
                setActionError(null);
              }}
            >
              <option value="">
                Все ответы
                {allLogsQuery.meta ? ` (${allLogsQuery.meta.total})` : ""}
              </option>
              {submissions.map((submission) => (
                <option key={submission.id} value={submission.id}>
                  #{submission.id}
                  {submission.createdAt || submission.created_at
                    ? ` · ${formatDateTime(
                        submission.createdAt ?? submission.created_at,
                      )}`
                    : ""}
                </option>
              ))}
            </SelectInput>
          </div>
        </div>
      </div>

      {actionMessage ? (
        <p className="mx-4 mt-3 font-onest text-xs text-emerald-700">
          {actionMessage}
        </p>
      ) : null}

      {actionError ? (
        <div
          className="mx-4 mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{actionError}</p>
        </div>
      ) : null}

      {error ? (
        <div
          className="mx-4 mt-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3"
          role="alert"
        >
          <p className="font-onest text-sm text-red-700">{error}</p>
        </div>
      ) : null}

      {isLoading && logs.length === 0 ? (
        <div className="mx-4 mt-4 animate-pulse space-y-3">
          <div className="h-10 rounded-lg bg-slate-100" />
          <div className="h-24 rounded-lg bg-slate-100" />
        </div>
      ) : logs.length === 0 ? (
        <div className="px-4 py-10 text-center">
          <p className="font-onest text-sm text-slate-600">
            {singleSubmissionView
              ? `Для ответа #${filterSubmissionId} пока нет записей в журнале`
              : "Пока нет записей в журнале вебхуков"}
          </p>
        </div>
      ) : (
        <div className="mx-4 mt-4 overflow-x-auto rounded-xl border border-slate-200/80">
          <table className="w-full min-w-[40rem] table-fixed border-collapse">
            <colgroup>
              {singleSubmissionView ? null : <col className="w-[4.5rem]" />}
              <col className="w-[4.5rem]" />
              <col />
              <col className="w-[4.5rem]" />
              <col className="w-[4.5rem]" />
              <col className="w-[9rem]" />
              <col className="w-14" />
            </colgroup>
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/70">
                {singleSubmissionView ? null : (
                  <th className="w-[4.5rem] px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Ответ
                  </th>
                )}
                <th className="w-[4.5rem] px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Попытка
                </th>
                <th className="px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  URL
                </th>
                <th className="w-[4.5rem] px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Статус
                </th>
                <th className="w-[4.5rem] px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Время
                </th>
                <th className="w-[9rem] px-3 py-2 text-left font-onest text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Дата
                </th>
                <th className="w-14 py-2 pl-2 pr-4" aria-label="Действия" />
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const rowKey = hookLogRowKey(log);
                return (
                  <HookLogTableRow
                    key={rowKey}
                    log={log}
                    showSubmission={!singleSubmissionView}
                    expanded={expandedKey === rowKey}
                    isRetrying={retryingKey === rowKey}
                    onToggle={() =>
                      setExpandedKey((current) =>
                        current === rowKey ? null : rowKey,
                      )
                    }
                    onFilterSubmission={onFilterSubmission}
                    onRetry={() => handleRowRetry(log)}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!singleSubmissionView && logs.length > 0 ? (
        <FormsListPagination
          meta={meta}
          page={page}
          perPage={perPage}
          perPageOptions={HOOK_LOGS_PER_PAGE_OPTIONS}
          isLoading={isLoading}
          onPageChange={setPage}
          onPerPageChange={handlePerPageChange}
        />
      ) : null}
    </div>
  );
}
